import {produce} from 'immer';
import get from 'lodash/get';
import set from 'lodash/set';
import unset from 'lodash/unset';

import {MigrationApplyFn} from '@/engine/types/model/migration';
import {logger} from '@/initializers/logger';
import {Predicate} from '@/model/types/migrations/condition';
import {MigrationOperation, MigrationOpType} from '@/model/types/migrations/operation';
import {Model} from '../../types';
import {applyForField, globMatchPath} from '../utils/object';
import {createOperationPredicate} from './conditions';

export type OperationFactory<T extends MigrationOpType> = (
  migration: Extract<MigrationOperation, {type: T}>,
  predicate: Predicate,
  model: Model,
) => MigrationApplyFn;

type MigrationMap = {
  [T in MigrationOpType]: OperationFactory<T>;
};

const LogOperation: OperationFactory<'log'> = ({messages}) => {
  return data => {
    logger.log(...messages);
    return data;
  };
};

const SetOperation: OperationFactory<'set'> = ({field, value}, predicate) =>
  produce(draft => {
    applyForField(draft, field, path => {
      if (predicate(get(draft, path))) {
        set(draft, path, value);
      }
    });
  });

const escapeRegExp = (text: string): string => text.replaceAll(/[.+?^${}()|[\]\\]/g, String.raw`\$&`);

const CopyOperation: OperationFactory<'copy'> = ({from, to}, predicate) =>
  produce(draft => {
    if (!from.includes('*')) {
      const value = get(draft, from);
      if (predicate(value)) {
        set(draft, to, value);
      }
      return;
    }

    const fromRegExp = new RegExp(`^${from.split('*').map(escapeRegExp).join('([^.]*)')}$`);
    for (const path of globMatchPath(draft, from)) {
      const captures = fromRegExp.exec(path)?.slice(1) ?? [];
      const value = get(draft, path);
      if (predicate(value)) {
        let index = 0;
        set(
          draft,
          to.replaceAll('*', () => captures[index++] ?? ''),
          value,
        );
      }
    }
  });

const UnsetOperation: OperationFactory<'unset'> = ({field}, predicate) =>
  produce(draft => {
    applyForField(draft, field, path => {
      if (predicate(get(draft, path))) {
        unset(draft, path);
      }
    });
  });

const BatchSetOperation: OperationFactory<'batch_set'> = ({type, fields, ...cond}, predicate, model) => {
  return document => {
    return fields.reduce((doc, field) => {
      return migrationMap.set({type: 'set', field, ...cond}, predicate, model)(doc);
    }, document);
  };
};

const BatchUnsetOperation: OperationFactory<'batch_unset'> = ({type, fields, ...cond}, predicate, model) => {
  return document => {
    return fields.reduce((doc, field) => {
      return migrationMap.unset({type: 'unset', field, ...cond}, predicate, model)(doc);
    }, document);
  };
};

const migrationMap: MigrationMap = {
  log: LogOperation,
  set: SetOperation,
  unset: UnsetOperation,
  copy: CopyOperation,
  batch_set: BatchSetOperation,
  batch_unset: BatchUnsetOperation,
};

export const getOperationFactory = <K extends MigrationOpType>(migration: Extract<MigrationOperation, {type: K}>) => {
  if (!migrationMap[migration.type]) {
    throw new Error(`Unknown action type: ${migration.type}`);
  }

  const predicate = createOperationPredicate(migration);

  return (model: Model) => migrationMap[migration.type](migration, predicate, model);
};
